import { useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes, elevation } from "@similarweb/styles";

import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

import { getEmployeeProfilesColumns, type EmployeeProfilesColumnHandlers } from "./columns";
import { SELECTION_COLUMN_WIDTH, StyledTableCardWrapper } from "./styles";

const PAGE_SIZE = 20;

type EmployeeProfilesTableProps = {
    rows: ProfileDto[];
    handlers: EmployeeProfilesColumnHandlers;
    dataAutomation: string;
    title: string;
    subtitle?: string;
    showCreatorName?: boolean;
    canManageRows?: boolean;
    isDeletingSelected?: boolean;
    /** Bumping this value clears the selection, e.g. once a bulk delete has gone through. */
    clearSelectionToken?: number;
    onDeleteSelected?: (ids: string[]) => void;
};

export const EmployeeProfilesTable = ({
    rows,
    handlers,
    dataAutomation,
    title,
    subtitle,
    showCreatorName = false,
    canManageRows = false,
    isDeletingSelected = false,
    clearSelectionToken = 0,
    onDeleteSelected,
}: EmployeeProfilesTableProps) => {
    const { translate } = useTranslate();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useEffect(() => {
        setSelectedIds([]);
    }, [clearSelectionToken]);

    const columnConfig = useMemo(
        () =>
            getEmployeeProfilesColumns(translate, {
                handlers,
                showCreatorName,
                showRowActions: canManageRows,
            }),
        [translate, handlers, showCreatorName, canManageRows],
    );

    return (
        <StyledTableCardWrapper
            borderRadius="6px"
            background={colorsPalettes.carbon["0"]}
            boxShadow={elevation.Z1}
            overflow="hidden"
            data-automation={dataAutomation}
        >
            <Table<ProfileDto>
                data={rows}
                rowKey="id"
                columnConfig={columnConfig}
                pagination={{ pageSize: PAGE_SIZE }}
                withArrowButtons={false}
                selection={
                    canManageRows
                        ? {
                              enabled: true,
                              columnWidth: SELECTION_COLUMN_WIDTH,
                              fixed: true,
                              selectedRowKeys: selectedIds,
                              onChange: (keys) => setSelectedIds(keys.map((key) => String(key))),
                          }
                        : undefined
                }
            >
                <Table.TableTop borderBottom>
                    <Table.TableTop.Title
                        title={title}
                        subtitle={subtitle}
                    />
                    <Table.TableTop.Selection
                        enabled={canManageRows}
                        selectText={translate("si.employee-profiles.selected_count")}
                        actionButtons={[
                            {
                                iconName: "delete",
                                children: translate("si.common.button.delete"),
                                onClick: () => onDeleteSelected?.(selectedIds),
                                isLoading: isDeletingSelected,
                                dataAutomation: "employee-profiles-delete-selected",
                            },
                        ]}
                    />
                </Table.TableTop>
            </Table>
        </StyledTableCardWrapper>
    );
};
